import React, { useEffect, useState, useCallback } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { formatDate } from "@/lib/formatDate";
import { useNavigate } from "react-router-dom";
import { apiClient, authFetch } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileSignature, Plus, FileDown, Mail, ArrowRightLeft, CheckCircle2, XCircle, Trash2 , ClipboardCheck } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";

const VAT_RATES = [
  { label: "0%", value: 0 },
  { label: "2.6%", value: 0.026 },
  { label: "8.1%", value: 0.081 },
];

const STATUS_LABELS = {
  draft: { label: "Entwurf", color: "bg-slate-100 text-slate-700" },
  sent: { label: "Versendet", color: "bg-blue-100 text-blue-700" },
  accepted: { label: "Angenommen", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Abgelehnt", color: "bg-rose-100 text-rose-700" },
  converted: { label: "Verrechnet", color: "bg-violet-100 text-violet-700" },
  ordered: { label: "Auftrag bestätigt", color: "bg-sky-100 text-sky-700" },
  expired: { label: "Abgelaufen", color: "bg-amber-100 text-amber-700" },
};

export default function Quotes() {
  const { company } = useCompany();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const plus30 = new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10);

  const blankForm = {
    customer_id: "",
    quote_number: "",
    issue_date: today,
    valid_until: plus30,
    currency: company?.currency || "CHF",
    vat_rate: 0.081,
    notes: "Gültig 30 Tage. Preise inkl. MWST.",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
  };
  const [form, setForm] = useState(blankForm);

  const cid = company?._id;

  const fetchQuotes = useCallback(async () => {
    if (!cid) return;
    try {
      const r = await apiClient.get(`/companies/${cid}/quotes`);
      setQuotes(r.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [cid]);

  const fetchCustomers = useCallback(async () => {
    if (!cid) return;
    try {
      const r = await apiClient.get(`/companies/${cid}/contacts`);
      setCustomers((r.data || []).filter((c) => c.type === "customer"));
    } catch (e) {
      console.error(e);
    }
  }, [cid]);

  useEffect(() => {
    fetchQuotes();
    fetchCustomers();
  }, [fetchQuotes, fetchCustomers]);

  const computeTotals = () => {
    const sub = form.line_items.reduce(
      (s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
      0,
    );
    const tax = +(sub * form.vat_rate).toFixed(2);
    return { subtotal: +sub.toFixed(2), tax_amount: tax, total: +(sub + tax).toFixed(2) };
  };

  const handleLineChange = (idx, key, val) => {
    const next = [...form.line_items];
    next[idx] = { ...next[idx], [key]: val };
    setForm({ ...form, line_items: next });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeLine = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setOpenDialog(true);
  };

  const openEdit = async (q) => {
    setEditingId(q._id);
    // Re-derive vat rate
    const sub = Number(q.subtotal) || 0;
    const tax = Number(q.tax_amount) || 0;
    const rate = sub > 0 ? +(tax / sub).toFixed(3) : 0.081;
    setForm({
      customer_id: q.customer_id,
      quote_number: q.quote_number,
      issue_date: q.issue_date,
      valid_until: q.valid_until,
      currency: q.currency || "CHF",
      vat_rate: rate,
      notes: q.notes || "",
      line_items: q.line_items || [{ description: "", quantity: 1, unit_price: 0 }],
    });
    setOpenDialog(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) {
      toast.error("Bitte einen Kunden auswählen.");
      return;
    }
    const totals = computeTotals();
    const payload = {
      customer_id: form.customer_id,
      quote_number: form.quote_number || "",
      issue_date: form.issue_date,
      valid_until: form.valid_until,
      currency: form.currency,
      line_items: form.line_items.map((it) => ({
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
        account_id: "3200",
      })),
      ...totals,
      status: "draft",
      notes: form.notes || null,
    };
    setLoading(true);
    try {
      if (editingId) {
        await apiClient.put(`/quotes/${editingId}`, payload);
        toast.success("Offerte aktualisiert.");
      } else {
        await apiClient.post(`/companies/${cid}/quotes`, payload);
        toast.success("Offerte erstellt.");
      }
      setOpenDialog(false);
      fetchQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Speichern.");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (q, status) => {
    try {
      await apiClient.post(`/quotes/${q._id}/status`, { status });
      toast.success(`Status geändert: ${STATUS_LABELS[status]?.label || status}`);
      fetchQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler.");
    }
  };

  const confirmOrder = async (q) => {
    if (!window.confirm(`Offerte ${q.quote_number} als Auftrag bestätigen?\nEs wird eine Auftragsbestätigung (AB) erstellt; Lieferscheine und Rechnung folgen unter «Aufträge».`)) return;
    try {
      const r = await apiClient.post(`/quotes/${q._id}/confirm-order`, {});
      toast.success(`Auftragsbestätigung ${r.data.order_number} erstellt.`);
      navigate("/orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Auftrag konnte nicht erstellt werden"); }
  };
  const convert = async (q) => {
    if (!window.confirm(`Offerte ${q.quote_number} in eine Rechnung umwandeln?\nAlle Positionen und Beträge werden übernommen.`)) return;
    try {
      const r = await apiClient.post(`/quotes/${q._id}/convert-to-invoice`, {});
      toast.success(`Rechnung ${r.data.invoice_number} erstellt.`);
      fetchQuotes();
      setTimeout(() => navigate(`/invoices?highlight=${r.data._id}`), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Umwandlung fehlgeschlagen.");
    }
  };

  const sendEmail = async (q) => {
    try {
      await apiClient.post(`/quotes/${q._id}/send-email`, {});
      toast.success("Offerte per E-Mail versendet.");
      fetchQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "E-Mail-Versand fehlgeschlagen.");
    }
  };

  const downloadPdf = (q) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q._id}/pdf`;
    authFetch(url)
      .then((r) => r.blob())
      .then((b) => {
        const blobUrl = URL.createObjectURL(b);
        window.open(blobUrl, "_blank");
      })
      .catch(() => toast.error("PDF konnte nicht geöffnet werden."));
  };

  const removeQuote = async (q) => {
    if (!window.confirm(`Offerte ${q.quote_number} wirklich löschen?`)) return;
    try {
      await apiClient.delete(`/quotes/${q._id}`);
      toast.success("Offerte gelöscht.");
      fetchQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const totals = computeTotals();
  const fmtMoney = (v) => `${form.currency} ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2 })}`;

  return (
    <div className="p-6 space-y-6" data-testid="quotes-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-semibold tracking-wider">
            <FileSignature className="h-4 w-4" />
            Verkauf
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Offerten</h1>
          <p className="text-sm text-slate-500 mt-1">
            Erstelle Angebote und wandle angenommene Offerten mit einem Klick in Rechnungen um.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="quote-new-button">
          <Plus className="h-4 w-4 mr-1" /> Neue Offerte
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {quotes.length === 0 ? (
          <EmptyState
            icon={<FileSignature className="h-12 w-12" />}
            title="Noch keine Offerten"
            description="Erstelle deine erste Offerte für einen Kunden. Bei Zusage kannst du sie mit einem Klick in eine Rechnung umwandeln."
            action={
              <Button onClick={openCreate} data-testid="quote-empty-create-button">
                <Plus className="h-4 w-4 mr-1" /> Offerte erstellen
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nr.</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Gültig bis</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => {
                const st = STATUS_LABELS[q.status] || { label: q.status, color: "bg-slate-100" };
                const canConvert = !["converted", "rejected", "expired"].includes(q.status);
                const canEdit = q.status !== "converted";
                return (
                  <TableRow key={q._id} data-testid={`quote-row-${q._id}`}>
                    <TableCell className="font-mono text-sm">{q.quote_number}</TableCell>
                    <TableCell>{q.customer_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(q.issue_date)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{q.valid_until ? formatDate(q.valid_until) : "–"}</TableCell>
                    <TableCell className="text-right font-medium">
                      {q.currency} {Number(q.total).toLocaleString("de-CH", { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <Badge className={st.color} data-testid={`quote-status-${q._id}`}>{st.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(q)} title="PDF" data-testid={`quote-pdf-${q._id}`}>
                          <FileDown className="h-3.5 w-3.5" />
                        </Button>
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => sendEmail(q)} title="E-Mail senden" data-testid={`quote-email-${q._id}`}>
                            <Mail className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {q.status === "sent" && (
                          <>
                            <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => setStatus(q, "accepted")} title="Akzeptiert markieren" data-testid={`quote-accept-${q._id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-rose-700" onClick={() => setStatus(q, "rejected")} title="Abgelehnt markieren" data-testid={`quote-reject-${q._id}`}>
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {canConvert && q.status !== "ordered" && (
                          <Button size="sm" variant="outline" onClick={() => confirmOrder(q)} title="Auftragsbestätigung erstellen (dann Lieferscheine → Rechnung)" className="text-sky-700 border-sky-300" data-testid={`quote-confirm-order-${q._id}`}>
                            <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Auftrag
                          </Button>
                        )}
                        {q.status === "ordered" && (
                          <Button size="sm" variant="outline" onClick={() => navigate("/orders")} className="text-sky-700" data-testid={`quote-open-order-${q._id}`}>
                            <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Zum Auftrag
                          </Button>
                        )}
                        {canConvert && q.status !== "ordered" && (
                          <Button size="sm" onClick={() => convert(q)} title="Direkt in Rechnung umwandeln" className="bg-violet-600 hover:bg-violet-700" data-testid={`quote-convert-${q._id}`}>
                            <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Rechnung
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => openEdit(q)} title="Bearbeiten" data-testid={`quote-edit-${q._id}`}>
                            Edit
                          </Button>
                        )}
                        {q.status !== "converted" && (
                          <Button size="sm" variant="outline" className="text-rose-700" onClick={() => removeQuote(q)} title="Löschen" data-testid={`quote-delete-${q._id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Offerte bearbeiten" : "Neue Offerte"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kunde</Label>
                <select
                  className="w-full h-10 rounded-md border border-slate-200 px-3"
                  value={form.customer_id}
                  onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                  data-testid="quote-customer-select"
                >
                  <option value="">— Kunde wählen —</option>
                  {customers.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Offerten-Nr. (leer = automatisch)</Label>
                <Input
                  value={form.quote_number}
                  onChange={(e) => setForm({ ...form, quote_number: e.target.value })}
                  placeholder="OFF-2026-001"
                  data-testid="quote-number-input"
                />
              </div>
              <div>
                <Label>Datum</Label>
                <SwissDateField  value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="quote-issue-date" />
              </div>
              <div>
                <Label>Gültig bis</Label>
                <SwissDateField  value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid-until" />
              </div>
              <div>
                <Label>Währung</Label>
                <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
              </div>
              <div>
                <Label>MWST-Satz</Label>
                <select
                  className="w-full h-10 rounded-md border border-slate-200 px-3"
                  value={form.vat_rate}
                  onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })}
                  data-testid="quote-vat-rate"
                >
                  {VAT_RATES.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Positionen</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="quote-add-line">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Position
                </Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`quote-line-${idx}`}>
                    <Input
                      className="col-span-6"
                      placeholder="Beschreibung"
                      value={it.description}
                      onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                    />
                    <Input
                      className="col-span-2"
                      type="number"
                      step="0.01"
                      placeholder="Menge"
                      value={it.quantity}
                      onChange={(e) => handleLineChange(idx, "quantity", e.target.value)}
                    />
                    <Input
                      className="col-span-3"
                      type="number"
                      step="0.01"
                      placeholder="Einzelpreis"
                      value={it.unit_price}
                      onChange={(e) => handleLineChange(idx, "unit_price", e.target.value)}
                    />
                    <Button type="button" size="sm" variant="outline" className="col-span-1 text-rose-700" onClick={() => removeLine(idx)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Bemerkungen / Konditionen</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                data-testid="quote-notes"
              />
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm" data-testid="quote-totals">
              <div className="flex justify-between"><span>Zwischensumme:</span><span className="font-mono">{fmtMoney(totals.subtotal)}</span></div>
              <div className="flex justify-between text-slate-500"><span>MWST:</span><span className="font-mono">{fmtMoney(totals.tax_amount)}</span></div>
              <div className="flex justify-between font-bold text-base pt-2 border-t border-slate-200"><span>Total:</span><span className="font-mono">{fmtMoney(totals.total)}</span></div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Abbrechen</Button>
              <Button type="submit" disabled={loading} data-testid="quote-submit-button">
                {loading ? "Speichern…" : editingId ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
